import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Radio, Circle, Clock, Users, ChevronRight, Trash2, Loader2, Film, MonitorPlay } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { studioApi, errMessage } from "@/lib/studioApi";
import { PlateLibrary } from "@/components/studio/PlateLibrary";
import { SimulcastPanel } from "@/components/studio/SimulcastPanel";
import { DateTimePicker } from "@/components/DateTimePicker";
import { pacificEpoch, shortDate } from "@/lib/nextFriday";
import { formatTime } from "@/lib/format";

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch (e) { return iso; }
}

function StatusBadge({ show }) {
  const rec = show.recording?.status, live = show.livestream?.status;
  if (show.status === "ended") return <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">Ended</span>;
  if (live === "live" || live === "starting") return <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary"><Radio className="h-3 w-3" /> Live</span>;
  if (rec === "recording" || rec === "starting") return <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-600"><Circle className="h-2.5 w-2.5 fill-current" /> Recording</span>;
  if (show.status === "live") return <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Room open</span>;
  return <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">Draft</span>;
}

export default function StudioHome() {
  const navigate = useNavigate();
  const [shows, setShows] = useState(null);
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", event_id: "", scheduled_for: "" });

  const load = () => studioApi.listShows().then(setShows).catch((e) => toast.error(errMessage(e)));
  useEffect(() => {
    load();
    studioApi.status().then(setStatus).catch(() => setStatus({ realtime: false, stream: false }));
    api.get("/events", { params: { upcoming: true } }).then((r) => setEvents(Array.isArray(r.data) ? r.data.slice(0, 12) : [])).catch(() => {});
  }, []);

  const pickEvent = (id) => {
    const ev = events.find((e) => e.event_id === id);
    const when = ev?.date ? new Date(pacificEpoch(ev.date, ev.start_time || "12:00")).toISOString() : null;
    setForm((f) => ({
      ...f,
      event_id: id,
      title: f.title || (ev ? `Friday Conversations — ${ev.title}` : ""),
      scheduled_for: when || f.scheduled_for,
    }));
  };

  const create = async () => {
    setCreating(true);
    try {
      const s = await studioApi.createShow({
        title: form.title.trim() || "Friday Conversations — Live Podcast",
        description: form.description || null,
        event_id: form.event_id || null,
        scheduled_for: form.scheduled_for || null,
      });
      toast.success("Show created");
      setOpen(false);
      navigate(`/studio/shows/${s.show_id}`);
    } catch (e) {
      toast.error(errMessage(e, "Could not create the show"));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? Recordings already saved to the library are kept.`)) return;
    try { await studioApi.deleteShow(s.show_id); toast.success("Show deleted"); load(); } catch (e) { toast.error(errMessage(e)); }
  };

  const active = (shows || []).filter((s) => s.status !== "ended");
  const past = (shows || []).filter((s) => s.status === "ended");

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="studio-home">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Studio</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">Produce the podcast</h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">One room for the host, guests and every camera. Switch sources, drop name plates, record to the cloud and go live to members — all from one screen.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-show-btn"><Plus className="mr-1.5 h-4 w-4" /> New show</Button>
      </div>

      {status && (!status.realtime || !status.stream) && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="studio-config-warning">
          {!status.realtime && <div>Cloudflare Realtime isn't configured — the studio room can't open.</div>}
          {!status.stream && <div>Cloudflare Stream isn't configured — going live to members is unavailable.</div>}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          { Icon: MonitorPlay, title: "1. Open the console", body: "Your laptop camera (the Owl) joins automatically. Share the guest and camera links." },
          { Icon: Users, title: "2. Admit & switch", body: "Admit guests from the lobby, click any source to put it on air, tap the tag for a name plate." },
          { Icon: Film, title: "3. Record & go live", body: "Cloud recording produces a real MP4 in your library. Go live streams to the member Hub." },
        ].map(({ Icon, title, body }) => (
          <div key={title} className="rounded-xl border bg-card p-4">
            <Icon className="h-5 w-5 text-primary" />
            <div className="mt-2 text-sm font-semibold">{title}</div>
            <div className="mt-1 text-xs text-muted-foreground">{body}</div>
          </div>
        ))}
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Upcoming & in progress</h2>
        {shows === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading shows…</div>
        ) : active.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="no-shows">No shows yet. Create one to open the studio.</div>
        ) : (
          <ul className="divide-y rounded-xl border bg-card" data-testid="active-shows">
            {active.map((s) => (
              <li key={s.show_id} className="flex items-center gap-3 px-4 py-3" data-testid={`show-row-${s.show_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><span className="truncate font-medium">{s.title}</span><StatusBadge show={s} /></div>
                  <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
                    {s.scheduled_for && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(s.scheduled_for)}</span>}
                    <span>Created {fmtDate(s.created_at)}{s.created_by_name ? ` · ${s.created_by_name}` : ""}</span>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(s)} title="Delete" data-testid={`delete-show-${s.show_id}`}><Trash2 className="h-4 w-4" /></Button>
                <Button asChild data-testid={`open-show-${s.show_id}`}><Link to={`/studio/shows/${s.show_id}`}>Open console <ChevronRight className="ml-1 h-4 w-4" /></Link></Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {past.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Past shows</h2>
          <ul className="divide-y rounded-xl border bg-card" data-testid="past-shows">
            {past.map((s) => (
              <li key={s.show_id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><span className="truncate font-medium">{s.title}</span><StatusBadge show={s} /></div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {fmtDate(s.ended_at || s.created_at)}
                    {s.recording?.status === "imported" && <span className="ml-2 text-emerald-700">· Recording saved</span>}
                    {s.recording?.status === "uploading" && <span className="ml-2 text-amber-700">· Recording processing</span>}
                    {s.recording?.status === "failed" && <span className="ml-2 text-red-600">· Recording failed</span>}
                  </div>
                </div>
                {s.recording?.recording_id && <Button asChild variant="outline" size="sm"><Link to={`/admin/recordings/${s.recording.recording_id}`}>Open recording</Link></Button>}
                <Button asChild variant="ghost" size="sm"><Link to={`/studio/shows/${s.show_id}`}>Details</Link></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(s)} title="Delete"><Trash2 className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <PlateLibrary />

      <section className="mt-8" data-testid="studio-simulcast-section">
        <h2 className="font-display text-lg font-semibold text-foreground">Simulcast</h2>
        <p className="mb-3 text-sm text-muted-foreground">Add YouTube (or Facebook / Twitch / any RTMP) once. Every show you take live is pushed there too — no extra software.</p>
        <SimulcastPanel />
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-show-dialog" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>New show</DialogTitle>
            <DialogDescription>Creates the studio room, a member Hub session, and guest/camera links.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {events.length > 0 && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Link to an event (optional)</label>
                <select value={form.event_id} onChange={(e) => pickEvent(e.target.value)} className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm" data-testid="new-show-event">
                  <option value="">— none —</option>
                  {events.map((ev) => <option key={ev.event_id} value={ev.event_id}>{ev.title}{ev.date ? ` · ${shortDate(ev.date)}${ev.start_time ? ` ${formatTime(ev.start_time)}` : ""}` : ""}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">Title</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Friday Conversations — Generosity" className="mt-1" data-testid="new-show-title" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Scheduled for (optional)</label>
              <div className="mt-1">
                <DateTimePicker value={form.scheduled_for} onChange={(iso) => setForm((f) => ({ ...f, scheduled_for: iso }))} placeholder="Pick a date & start time" testId="new-show-when" />
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">Shown in your local time zone. Pick the date, tap the hour and minute, then Done.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Notes (optional)</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Run of show, guest list, talking points…" className="mt-1" rows={3} data-testid="new-show-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={creating} data-testid="create-show-submit">{creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Create & open console</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
